import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, X, Camera, CheckCircle2, ImagePlus, Trash2, AlertTriangle } from "lucide-react";
import { useAuth, api, goToLogin } from "@/contexts/AuthContext";
import { AI_MODELS, AI_SOFTWARE_TOOLS, CATEGORIES, EXPERIENCE_LEVELS } from "@/data/mockPeople";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MAX_UPLOAD_BYTES = 600_000; // ~600KB decoded

// Downscale + JPEG-compress a picked image so profile photos stay well under
// the backend limit without asking the user to think about it.
async function fileToCompressedDataUrl(file, maxDim = 512, quality = 0.82) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale);
  const h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function MyProfilePage() {
  const { user, loading, refresh, setUser } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [name, setName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [bio, setBio] = useState("");
  const [tools, setTools] = useState([]); // {name, level} — AI Models
  const [softwareTools, setSoftwareTools] = useState([]); // string[]
  const [categories, setCategories] = useState([]); // string[]
  const [links, setLinks] = useState([]); // {label, url}
  const [photoDataUrl, setPhotoDataUrl] = useState(""); // preview only
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Tracked outside React state so it's readable synchronously inside the
  // redirect effect below. Prevents the effect from bouncing the user back to
  // Google right after they deleted their account.
  const justDeletedRef = useRef(false);

  useEffect(() => {
    if (justDeletedRef.current) return;
    if (!loading && !user) goToLogin("/my/profile");
  }, [loading, user]);

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setJobTitle(user.job_title || "");
    setCompany(user.company || "");
    setLocation(user.location || "");
    setBio(user.bio || "");
    setTools(user.tools || []);
    setSoftwareTools(user.software_tools || []);
    setCategories(user.categories || []);
    setLinks(user.links || []);
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#3FFFB6]" size={24} />
      </div>
    );
  }

  const isOnboarding = !user.profile_complete;
  const currentPicture = photoDataUrl || (user.picture_uploaded ? user.picture : "");

  const addTool = (n) => {
    if (tools.some((t) => t.name === n)) return;
    setTools([...tools, { name: n, level: "Intermediate" }]);
  };
  const setToolLevel = (n, level) =>
    setTools(tools.map((t) => (t.name === n ? { ...t, level } : t)));
  const removeTool = (n) => setTools(tools.filter((t) => t.name !== n));

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    setUploadingPhoto(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      if (dataUrl.length > MAX_UPLOAD_BYTES * 1.4) {
        toast.error("Image is huge — try a smaller one.");
        return;
      }
      setPhotoDataUrl(dataUrl);
      const { data } = await api.post("/me/photo", { data_url: dataUrl });
      await refresh();
      // Show the freshly saved photo (server echoes it back)
      setPhotoDataUrl(data.picture);
      toast.success("Photo saved.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't upload photo.";
      toast.error(msg);
    } finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const requirements = [
    { key: "name", ok: name.trim().length > 0, label: "Your name" },
    {
      key: "photo",
      ok: user.picture_uploaded || !!photoDataUrl,
      label: "A real photo (uploaded, not from Google)",
    },
    { key: "job_title", ok: jobTitle.trim().length > 0, label: "Job title" },
    { key: "company", ok: company.trim().length > 0, label: "Company" },
    {
      key: "tools",
      ok: tools.length > 0,
      label: "At least one AI model or tool with a level",
    },
  ];
  const missing = requirements.filter((r) => !r.ok);

  const saveProfile = async () => {
    if (missing.length > 0) {
      toast.error(`Still needed: ${missing[0].label}.`);
      return;
    }
    setSaving(true);
    try {
      await api.patch("/me", {
        name: name.trim(),
        job_title: jobTitle.trim(),
        company: company.trim(),
        location: location.trim(),
        bio: bio.trim(),
        tools,
        software_tools: softwareTools,
        categories,
        links: links
          .map((l) => ({ label: (l.label || "").trim(), url: (l.url || "").trim() }))
          .filter((l) => l.url),
      });
      await refresh();
      toast.success(
        isOnboarding
          ? "Welcome to Oiiister — your profile is live."
          : "Profile saved."
      );
      if (isOnboarding) navigate("/");
    } catch (e) {
      toast.error("Couldn't save profile.");
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/me");
      // Guard the redirect effect BEFORE clearing state so it doesn't bounce
      // us to Google. Then navigate away first — that unmounts this page — and
      // only then clear the auth user so the sidebar re-renders as logged-out.
      justDeletedRef.current = true;
      setDeleteOpen(false);
      navigate("/", { replace: true });
      setUser(null);
      toast.success("Your account has been deleted.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't delete account.";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-24">
      {isOnboarding ? (
        <>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D6FFEB] text-[#3FFFB6] text-xs font-semibold tracking-wider uppercase">
            <Camera size={12} />
            Finish setting up
          </span>
          <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#111827]">
            Create your Oiiister profile.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#4B5563] leading-relaxed max-w-2xl">
            Signing in with Google isn't enough. Tell us who you are so people
            can find you and know they're talking to a real human.
          </p>
        </>
      ) : (
        <>
          <h1 className="font-display font-bold text-3xl text-[#111827]">
            Your profile
          </h1>
          <p className="text-sm text-[#9CA3AF] mt-1">{user.email}</p>
        </>
      )}

      {isOnboarding && (
        <section
          data-testid="onboarding-checklist"
          className="mt-8 rounded-3xl bg-white ring-1 ring-[#F3F4F6] p-5"
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">
            What we still need
          </p>
          <ul className="mt-3 space-y-2">
            {requirements.map((r) => (
              <li
                key={r.key}
                data-testid={`req-${r.key}`}
                className={`flex items-center gap-2.5 text-sm ${
                  r.ok ? "text-[#065F46]" : "text-[#4B5563]"
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                    r.ok ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#9CA3AF]"
                  }`}
                >
                  {r.ok ? <CheckCircle2 size={12} /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                </span>
                {r.label}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-8 bg-white rounded-3xl ring-1 ring-[#F3F4F6] p-8">
        {/* Photo */}
        <div className="flex items-center gap-5">
          <div className="relative">
            {currentPicture ? (
              <img
                src={currentPicture}
                alt="Your profile"
                className="w-24 h-24 rounded-full object-cover ring-2 ring-white shadow-sm"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF]">
                <ImagePlus size={28} />
              </div>
            )}
            {uploadingPhoto && (
              <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                <Loader2 className="animate-spin text-white" size={20} />
              </div>
            )}
          </div>
          <div>
            <p className="font-display font-semibold text-lg text-[#111827]">
              Profile photo
            </p>
            <p className="text-sm text-[#4B5563] mt-0.5">
              Please upload a real photo of yourself — no logos or avatars.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              data-testid="onb-photo-input"
              className="hidden"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="onb-photo-upload"
              className="mt-3 h-10 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] text-sm font-semibold px-5"
            >
              <Camera size={14} className="mr-2" />
              {user.picture_uploaded || photoDataUrl ? "Change photo" : "Upload photo"}
            </Button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Your name</Label>
            <Input
              data-testid="onb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex Rivera"
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          <div>
            <Label>Location <span className="text-[#9CA3AF] font-normal">(optional)</span></Label>
            <Input
              data-testid="onb-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Berlin, DE"
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          <div>
            <Label>Job title</Label>
            <Input
              data-testid="onb-job-title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Head of Growth"
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          <div>
            <Label>Company name</Label>
            <Input
              data-testid="onb-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g. Northwind Labs"
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
        </div>

        <div className="mt-6">
          <Label>Short bio <span className="text-[#9CA3AF] font-normal">(optional)</span></Label>
          <Textarea
            data-testid="onb-bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell people who you are and what kinds of AI problems you take on."
            className="mt-1.5 min-h-24 rounded-xl"
          />
        </div>

        <div className="mt-8">
          <p className="text-sm font-semibold text-[#111827]">
            Categories you work in
          </p>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            Pick the problem areas you've solved (or love to solve). Helps people find the right expert.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            {CATEGORIES.map((n) => {
              const active = categories.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() =>
                    setCategories(
                      active
                        ? categories.filter((x) => x !== n)
                        : [...categories, n]
                    )
                  }
                  data-testid={`onb-category-${n}`}
                  className={`inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium ring-1 transition-colors ${
                    active
                      ? "bg-[#3FFFB6] text-[#111827] ring-[#3FFFB6]"
                      : "bg-white text-[#4B5563] ring-[#E5E7EB] hover:ring-[#3FFFB6] hover:text-[#3FFFB6]"
                  }`}
                >
                  {active ? null : <Plus size={12} />}
                  {n}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-8">
          <p className="text-sm font-semibold text-[#111827]">
            AI Models you use
          </p>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            Pick every model you actually use, then set your expertise level. Can't find it? Submit your own below.
          </p>

          {tools.length > 0 && (
            <ul className="mt-4 space-y-2">
              {tools.map((t) => (
                <li
                  key={t.name}
                  data-testid={`onb-tool-row-${t.name}`}
                  className="flex items-center gap-3 bg-[#FAFAFA] ring-1 ring-[#F3F4F6] rounded-2xl px-3 py-2"
                >
                  <span className="flex-1 text-sm font-medium text-[#111827]">
                    {t.name}
                  </span>
                  <Select
                    value={t.level}
                    onValueChange={(v) => setToolLevel(t.name, v)}
                  >
                    <SelectTrigger className="h-8 w-40 rounded-lg text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPERIENCE_LEVELS.map((lvl) => (
                        <SelectItem key={lvl} value={lvl}>
                          {lvl}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => removeTool(t.name)}
                    className="text-[#9CA3AF] hover:text-[#B91C1C]"
                    aria-label={`Remove ${t.name}`}
                  >
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap gap-2 items-center">
            {AI_MODELS.filter((n) => !tools.some((t) => t.name === n)).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => addTool(n)}
                data-testid={`onb-tool-add-${n}`}
                className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium bg-white ring-1 ring-[#E5E7EB] text-[#4B5563] hover:ring-[#3FFFB6] hover:text-[#3FFFB6] transition-colors"
              >
                <Plus size={12} /> {n}
              </button>
            ))}
            <CustomToolInput
              onAdd={(n) => addTool(n)}
              taken={tools.map((t) => t.name)}
            />
          </div>
        </div>

        {/* AI Software Building Tools — plain checklist, no per-item level */}
        <div className="mt-10">
          <p className="text-sm font-semibold text-[#111827]">
            AI Software Building Tools
          </p>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            Which AI coding tools do you actually use? (optional)
          </p>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            {AI_SOFTWARE_TOOLS.map((n) => {
              const active = softwareTools.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() =>
                    setSoftwareTools(
                      active
                        ? softwareTools.filter((x) => x !== n)
                        : [...softwareTools, n]
                    )
                  }
                  data-testid={`onb-software-${n}`}
                  className={`inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium ring-1 transition-colors ${
                    active
                      ? "bg-[#3FFFB6] text-[#111827] ring-[#3FFFB6]"
                      : "bg-white text-[#4B5563] ring-[#E5E7EB] hover:ring-[#3FFFB6] hover:text-[#3FFFB6]"
                  }`}
                >
                  {active ? null : <Plus size={12} />}
                  {n}
                </button>
              );
            })}
            {/* Any custom entries the user added that aren't in the seed list */}
            {softwareTools
              .filter((n) => !AI_SOFTWARE_TOOLS.includes(n))
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() =>
                    setSoftwareTools(softwareTools.filter((x) => x !== n))
                  }
                  data-testid={`onb-software-custom-${n}`}
                  className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium bg-[#3FFFB6] text-[#111827] ring-1 ring-[#3FFFB6]"
                >
                  {n}
                </button>
              ))}
            <CustomSoftwareInput
              onAdd={(n) => {
                if (!softwareTools.includes(n)) setSoftwareTools([...softwareTools, n]);
              }}
              taken={softwareTools}
            />
          </div>
        </div>

        <div className="mt-10">
          <p className="text-sm font-semibold text-[#111827]">
            Links{" "}
            <span className="text-[#9CA3AF] font-normal">(optional)</span>
          </p>
          <p className="text-xs text-[#9CA3AF] mt-0.5">
            Add up to 10 links — your portfolio, work samples, business site,
            LinkedIn, GitHub, anything you want people to see.
          </p>

          {links.length > 0 && (
            <ul className="mt-4 space-y-2">
              {links.map((l, idx) => (
                <li
                  key={idx}
                  data-testid={`onb-link-row-${idx}`}
                  className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-[#FAFAFA] ring-1 ring-[#F3F4F6] rounded-2xl p-2"
                >
                  <Input
                    data-testid={`onb-link-label-${idx}`}
                    value={l.label}
                    onChange={(e) =>
                      setLinks(
                        links.map((x, i) =>
                          i === idx ? { ...x, label: e.target.value } : x
                        )
                      )
                    }
                    placeholder="Label (e.g. Portfolio)"
                    className="h-9 rounded-lg text-sm sm:w-48 sm:flex-none"
                  />
                  <Input
                    data-testid={`onb-link-url-${idx}`}
                    value={l.url}
                    onChange={(e) =>
                      setLinks(
                        links.map((x, i) =>
                          i === idx ? { ...x, url: e.target.value } : x
                        )
                      )
                    }
                    placeholder="https://your-link.com"
                    className="h-9 rounded-lg text-sm flex-1"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setLinks(links.filter((_, i) => i !== idx))
                    }
                    data-testid={`onb-link-remove-${idx}`}
                    className="w-9 h-9 rounded-lg text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-white inline-flex items-center justify-center shrink-0"
                    aria-label="Remove link"
                  >
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {links.length < 10 && (
            <button
              type="button"
              onClick={() => setLinks([...links, { label: "", url: "" }])}
              data-testid="onb-link-add"
              className="mt-3 inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium bg-white ring-1 ring-dashed ring-[#B8FFDD] text-[#3FFFB6] hover:ring-[#3FFFB6] transition-colors"
            >
              <Plus size={14} /> Add a link
            </button>
          )}
        </div>

        <Button
          onClick={saveProfile}
          disabled={saving || missing.length > 0}
          data-testid="onb-save"
          className="mt-10 w-full sm:w-auto h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF] text-[#111827] font-semibold px-8"
        >
          {saving
            ? "Saving…"
            : isOnboarding
            ? missing.length > 0
              ? `Complete ${missing.length} more field${missing.length === 1 ? "" : "s"}`
              : "Finish and enter Oiiister"
            : "Save profile"}
        </Button>
      </section>

      {/* Danger zone — only for users who have a real (complete) profile.
          Onboarding users have nothing to delete beyond a Google identity. */}
      {!isOnboarding && (
        <section
          data-testid="danger-zone"
          className="mt-10 rounded-3xl ring-1 ring-[#FEE2E2] bg-[#FEF2F2] p-6 sm:p-8"
        >
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-xl bg-[#FEE2E2] text-[#B91C1C] flex items-center justify-center shrink-0">
              <AlertTriangle size={16} />
            </span>
            <div className="min-w-0">
              <p className="font-display font-semibold text-base text-[#111827]">
                Delete your account
              </p>
              <p className="text-sm text-[#4B5563] mt-1 leading-relaxed">
                Removes your profile, photo, links, posted problems, submitted
                solutions and follows. This can't be undone. If you sign in
                again later you'll start fresh.
              </p>
              <Button
                type="button"
                onClick={() => {
                  setDeleteConfirm("");
                  setDeleteOpen(true);
                }}
                data-testid="delete-account-open"
                className="mt-4 h-10 rounded-full bg-white ring-1 ring-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEE2E2] hover:ring-[#B91C1C] font-semibold px-5"
              >
                <Trash2 size={14} className="mr-2" />
                Delete my account
              </Button>
            </div>
          </div>
        </section>
      )}

      <Dialog open={deleteOpen} onOpenChange={(v) => !deleting && setDeleteOpen(v)}>
        <DialogContent data-testid="delete-account-modal" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#B91C1C] flex items-center gap-2">
              <AlertTriangle size={18} />
              Delete your account?
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm text-[#4B5563] leading-relaxed">
              This permanently removes your Oiiister profile, photo, links,
              posted problems, submitted solutions and follows. It cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Label htmlFor="delete-confirm" className="text-xs font-semibold uppercase tracking-wider text-[#9CA3AF]">
              Type <span className="text-[#111827]">DELETE</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              data-testid="delete-account-confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              className="mt-2 h-11 rounded-xl"
              autoComplete="off"
            />
          </div>
          <div className="mt-6 flex items-center justify-end gap-2">
            <Button
              type="button"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              className="h-10 rounded-full bg-white ring-1 ring-[#E5E7EB] text-[#111827] hover:ring-[#111827] font-semibold px-5"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={deleteAccount}
              disabled={deleting || deleteConfirm.trim() !== "DELETE"}
              data-testid="delete-account-confirm-btn"
              className="h-10 rounded-full bg-[#B91C1C] hover:bg-[#991B1B] disabled:bg-[#FCA5A5] disabled:cursor-not-allowed text-white font-semibold px-5"
            >
              {deleting ? "Deleting…" : "Delete forever"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inline input for typing an AI Product not present in the seed list.
function CustomToolInput({ onAdd, taken }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (taken.some((t) => t.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      setOpen(false);
      return;
    }
    onAdd(v);
    setDraft("");
    setOpen(false);
  };
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="onb-tool-submit-open"
        className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium bg-white ring-1 ring-dashed ring-[#B8FFDD] text-[#3FFFB6] hover:ring-[#3FFFB6] transition-colors"
      >
        <Plus size={12} /> Submit an AI Product
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-white ring-1 ring-[#3FFFB6] rounded-full pl-3 pr-1 h-8">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") { setOpen(false); setDraft(""); }
        }}
        placeholder="e.g. Grok, Mistral, DeepSeek"
        data-testid="onb-tool-submit-input"
        className="text-xs h-6 outline-none min-w-40 placeholder:text-[#9CA3AF]"
      />
      <button
        type="button"
        onClick={commit}
        data-testid="onb-tool-submit-confirm"
        className="w-6 h-6 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] inline-flex items-center justify-center"
        aria-label="Add"
      >
        <Plus size={12} />
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setDraft(""); }}
        className="w-6 h-6 rounded-full text-[#9CA3AF] hover:text-[#111827] inline-flex items-center justify-center"
        aria-label="Cancel"
      >
        <X size={12} />
      </button>
    </span>
  );
}

// Same shape as CustomToolInput but re-used for the software-tools list.
// Kept as a separate component so testids and placeholders stay meaningful.
function CustomSoftwareInput({ onAdd, taken }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (taken.some((t) => t.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      setOpen(false);
      return;
    }
    onAdd(v);
    setDraft("");
    setOpen(false);
  };
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="onb-software-submit-open"
        className="inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium bg-white ring-1 ring-dashed ring-[#B8FFDD] text-[#3FFFB6] hover:ring-[#3FFFB6] transition-colors"
      >
        <Plus size={12} /> Submit a software tool
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-white ring-1 ring-[#3FFFB6] rounded-full pl-3 pr-1 h-8">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") { setOpen(false); setDraft(""); }
        }}
        placeholder="e.g. Custom IDE plugin"
        data-testid="onb-software-submit-input"
        className="text-xs h-6 outline-none min-w-40 placeholder:text-[#9CA3AF]"
      />
      <button
        type="button"
        onClick={commit}
        data-testid="onb-software-submit-confirm"
        className="w-6 h-6 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] inline-flex items-center justify-center"
        aria-label="Add"
      >
        <Plus size={12} />
      </button>
      <button
        type="button"
        onClick={() => { setOpen(false); setDraft(""); }}
        className="w-6 h-6 rounded-full text-[#9CA3AF] hover:text-[#111827] inline-flex items-center justify-center"
        aria-label="Cancel"
      >
        <X size={12} />
      </button>
    </span>
  );
}
